/**
 * CR-4161: Credit Score Tab for Supplier Profile
 * Displays credit score records with CRUD functionality
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, Pencil, Trash2, FileText, X, Save } from 'lucide-react';
import { suppliersAPI, type SupplierProfile, type CreditScore } from '../../lib/api';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import FileDropZone from '../../components/common/FileDropZone';

interface CreditScoreTabProps {
  supplier: SupplierProfile;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function getScoreBadge(score: number) {
  if (score >= 800) return { label: 'Excellent', className: 'bg-green-100 text-green-800' };
  if (score >= 700) return { label: 'Good', className: 'bg-blue-100 text-blue-800' };
  if (score >= 600) return { label: 'Fair', className: 'bg-yellow-100 text-yellow-800' };
  return { label: 'Poor', className: 'bg-red-100 text-red-800' };
}

export function CreditScoreTab({ supplier }: CreditScoreTabProps) {
  const confirm = useConfirm();
  const [scores, setScores] = useState<CreditScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingScore, setEditingScore] = useState<CreditScore | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  // Form state
  const [formScore, setFormScore] = useState('');
  const [formComments, setFormComments] = useState('');
  const [formFile, setFormFile] = useState<File | null>(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);

  const fetchScores = async () => {
    try {
      setLoading(true);
      const response = await suppliersAPI.getCreditScores({
        supplier: supplier.company,
        page,
        page_size: pageSize,
        ordering: '-created_at',
      });
      setScores(response.data.results);
      setTotalCount(response.data.count);
    } catch (err: unknown) {
      console.error('Failed to fetch credit scores:', err);
      toast.error('Failed to load credit scores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.company, page, pageSize]);

  const openAddModal = () => {
    setEditingScore(null);
    setFormScore('');
    setFormComments('');
    setFormFile(null);
    setRemoveAttachment(false);
    setModalOpen(true);
  };

  const openEditModal = (score: CreditScore) => {
    setEditingScore(score);
    setFormScore(String(score.score));
    setFormComments(score.comments);
    setFormFile(null);
    setRemoveAttachment(false);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const scoreValue = parseInt(formScore, 10);
    if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > 999) {
      toast.error('Credit score must be between 0 and 999');
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('score', String(scoreValue));
      formData.append('comments', formComments);
      formData.append('supplier', String(supplier.company));
      if (formFile) {
        formData.append('attachment', formFile);
      } else if (editingScore && removeAttachment) {
        // CR-4824: clear existing attachment without requiring a replacement
        formData.append('attachment', '');
      }

      if (editingScore) {
        await suppliersAPI.updateCreditScore(editingScore.id, formData);
        toast.success('Credit score updated');
      } else {
        await suppliersAPI.createCreditScore(formData);
        toast.success('Credit score added');
      }

      setModalOpen(false);
      fetchScores();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(error.response?.data?.detail || 'Failed to save credit score');
    } finally {
      setSaving(false);
    }
  };

  const [viewingAttachment, setViewingAttachment] = useState<number | null>(null);

  // CR-5771: /media/credit_scores/* is blocked by block_private_media and a
  // plain <a href> wouldn't carry the auth Token header anyway. Fetch via
  // the authenticated download endpoint, materialise as a blob, and open in
  // a new tab so PDFs / images still render inline (Content-Disposition on
  // the response decides inline vs. download).
  const handleViewAttachment = async (score: CreditScore) => {
    try {
      setViewingAttachment(score.id);
      const response = await suppliersAPI.downloadCreditScoreAttachment(score.id);
      const rawContentType = response.headers['content-type'];
      const contentType = typeof rawContentType === 'string' ? rawContentType : 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Popup blocked — fall back to triggering a download so the user
        // still gets the file.
        const link = document.createElement('a');
        link.href = url;
        link.rel = 'noopener noreferrer';
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err: unknown) {
      console.error('Failed to open credit score attachment:', err);
      const error = err as { response?: { status?: number; data?: { detail?: string } } };
      const fallback = error.response?.status === 404
        ? 'Attachment is no longer available'
        : 'Failed to open attachment';
      toast.error(error.response?.data?.detail || fallback);
    } finally {
      setViewingAttachment(null);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ title: 'Delete Credit Score', description: 'Are you sure you want to delete this credit score entry?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      setDeleting(id);
      await suppliersAPI.deleteCreditScore(id);
      toast.success('Credit score deleted');
      fetchScores();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(error.response?.data?.detail || 'Failed to delete credit score');
    } finally {
      setDeleting(null);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateStr: string) => {
    return contextFormatDate(dateStr);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Credit Scores</CardTitle>
          <Button size="sm" onClick={openAddModal}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : scores.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No credit score records yet</p>
              <p className="text-sm mt-1">Click "Add" to create the first entry</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">ID</th>
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Credit Score</th>
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Date Added</th>
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Comments</th>
                      <th className="pb-2 pr-4 font-medium text-muted-foreground">Created By</th>
                      <th className="pb-2 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.map((score) => {
                      const badge = getScoreBadge(score.score);
                      return (
                        <tr key={score.id} className="border-b last:border-0">
                          <td className="py-3 pr-4 text-muted-foreground">#{score.id}</td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{score.score}</span>
                              <Badge className={badge.className}>{badge.label}</Badge>
                            </div>
                          </td>
                          <td className="py-3 pr-4">{formatDate(score.created_at)}</td>
                          <td className="py-3 pr-4 max-w-[200px] truncate" title={score.comments}>
                            {score.comments || '-'}
                          </td>
                          <td className="py-3 pr-4">{score.created_by_name || '-'}</td>
                          <td className="py-3">
                            <div className="flex items-center gap-1">
                              {score.attachment && (
                                <button
                                  type="button"
                                  onClick={() => handleViewAttachment(score)}
                                  disabled={viewingAttachment === score.id}
                                  className="p-1 hover:bg-muted rounded disabled:opacity-50"
                                  title="View attachment"
                                >
                                  {viewingAttachment === score.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                  ) : (
                                    <FileText className="h-4 w-4 text-blue-600" />
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => openEditModal(score)}
                                className="p-1 hover:bg-muted rounded"
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(score.id)}
                                disabled={deleting === score.id}
                                className="p-1 hover:bg-red-50 rounded text-red-600 disabled:opacity-50"
                                title="Delete"
                              >
                                {deleting === score.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <span>of {totalCount} entries</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="px-3 text-sm">Page {page} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-background rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold">
                {editingScore ? 'Edit Credit Score' : 'Add Credit Score'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="p-1 hover:bg-muted rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Credit Score *</label>
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={formScore}
                  onChange={(e) => setFormScore(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. 750"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Comments</label>
                <textarea
                  value={formComments}
                  onChange={(e) => setFormComments(e.target.value)}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                  placeholder="Optional notes about this credit score"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Attachment (optional)</label>
                {editingScore?.attachment && !formFile && removeAttachment ? (
                  <div className="flex items-center justify-between border border-dashed rounded-lg px-3 py-2 text-sm">
                    <span className="text-muted-foreground">
                      Attachment will be removed on save.
                    </span>
                    <button
                      type="button"
                      onClick={() => setRemoveAttachment(false)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  // Customer review 11 Sep (drag and drop everywhere).
                  <FileDropZone
                    size="compact"
                    hint={formFile ? <>Selected: <span className="font-medium text-foreground">{formFile.name}</span></> : undefined}
                    onFiles={(files) => { setFormFile(files[0]); setRemoveAttachment(false); }}
                    data-testid="credit-score-attachment-drop-zone"
                  />
                )}
                {editingScore?.attachment && !formFile && !removeAttachment && (
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-muted-foreground">
                      A file is currently attached. You can remove it or upload a new file to replace it.
                    </p>
                    <button
                      type="button"
                      onClick={() => setRemoveAttachment(true)}
                      className="ml-2 flex items-center gap-1 text-xs text-red-600 hover:underline"
                      title="Remove attachment"
                    >
                      <Trash2 className="h-3 w-3" />
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !formScore}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
